import { useState, useEffect } from 'react'
import { useQuery, gql, NetworkStatus } from '@apollo/client'
import { useLocation } from 'react-router-dom'
import clsx from 'clsx'

import { generateUserRankText, getProfilePic } from '../utils'

const GET_LEADERBOARD_USERS = gql`
  query LeaderboardUsers {
    leaderboardUsers {
      firstName
      lastName
      points
      location
      profilePic
    }
  }
`

const Leaderboard = () => {
  let location = useLocation()
  const { data, refetch, networkStatus, loading } = useQuery(
    GET_LEADERBOARD_USERS,
    {
      notifyOnNetworkStatusChange: true,
    }
  )
  const [topWinners, setTopWinners] = useState([])

  useEffect(() => {
    if (!data) {
      return
    }

    const getWinnersProfilePic = async (topWinners) => {
      let finalTopWinners = JSON.parse(JSON.stringify(topWinners))

      for (let i = 0; i < topWinners.length; i++) {
        try {
          const profilePicURL = await getProfilePic(topWinners[i].profilePic)

          if (profilePicURL) {
            finalTopWinners[i]['profilePicURL'] = profilePicURL
          }
        } catch (_) {
          finalTopWinners[i]['profilePicURL'] = ''
        }
      }

      return finalTopWinners
    }

    // Pick only the top 3 winners
    let newTopWinners = data?.leaderboardUsers.slice(0, 3)

    // If there are no 3 top winners yet, populate a dummy objects so it shows
    // up on the leaderboard.
    if (newTopWinners.length < 3) {
      for (let i = newTopWinners.length; i < 3; i++) {
        newTopWinners.push({})
      }
    }

    // Swap the position so the 1st winner is in the middle
    ;[newTopWinners[0], newTopWinners[1]] = [newTopWinners[1], newTopWinners[0]]

    // Grab top winners' profile picture
    getWinnersProfilePic(newTopWinners).then((finalTopWinners) => {
      // Finally, update the top winners state
      setTopWinners(finalTopWinners)
    })
    setTopWinners(newTopWinners)
  }, [data])

  useEffect(() => {
    // Refetch when user navigates back to this page
    refetch()
  }, [location, refetch])

  return (
    <main className="min-h-[calc(100vh-200px)]">
      <div className="container mb-[120px] max-w-screen-leaderboard pt-14 sm:px-0">
        <div className="pb-[10px]">
          <h1 className="mb-[80px] text-center text-4xl font-bold text-skin-accent">
            leaderboard.
          </h1>
          {/** Top 3 */}
          <div>
            {topWinners.length === 0 ||
            networkStatus === NetworkStatus.refetch ? (
              <LoadingSkeleton />
            ) : (
              <div className="flex flex-row justify-between">
                {topWinners.map((topWinner, index) => {
                  const rank = index === 0 ? 2 : index === 1 ? 1 : 3

                  return (
                    <div
                      key={`${index}${topWinner.firstName ?? '-'}`}
                      className={`flex flex-col items-center text-center ${
                        rank === 1 ? '-mt-[70px]' : ''
                      }`}
                    >
                      <span className="text-2xl font-bold text-flame">
                        {generateUserRankText(rank)}
                      </span>
                      <div
                        className={`my-4 flex items-center justify-center rounded-full border-[1px] border-light-gray bg-white ${
                          rank === 1
                            ? 'h-[137px] w-[137px] sm:h-[167px] sm:w-[167px]'
                            : 'h-[100px] w-[100px] sm:h-[130px] sm:w-[130px]'
                        }`}
                      >
                        {topWinner.profilePicURL ? (
                          <img
                            src={topWinner.profilePicURL}
                            alt=""
                            className="h-full w-full rounded-full"
                          />
                        ) : (
                          <div className="w-3/5 text-center text-xs leading-[16px] text-dark-gray">
                            USER'S PHOTO
                          </div>
                        )}
                      </div>
                      <span className="mt-2 max-w-[150px] text-2xl font-bold uppercase leading-none text-flame">
                        {topWinner.firstName}
                      </span>
                      <span className="mt-2 max-w-[150px] text-sm font-bold leading-none text-black-olive">
                        {topWinner.location}
                      </span>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/** Leaderboard */}
          <div className="mt-6 pr-4 text-right">
            <button
              disabled={loading}
              className={clsx(
                'group disabled:cursor-not-allowed',
                loading && 'animate-spin'
              )}
              onClick={() => refetch()}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="h-6 w-6 text-skin-accent group-enabled:hover:text-skin-accent-hover"
              >
                <path
                  fillRule="evenodd"
                  d="M4.755 10.059a7.5 7.5 0 0112.548-3.364l1.903 1.903h-3.183a.75.75 0 100 1.5h4.992a.75.75 0 00.75-.75V4.356a.75.75 0 00-1.5 0v3.18l-1.9-1.9A9 9 0 003.306 9.67a.75.75 0 101.45.388zm15.408 3.352a.75.75 0 00-.919.53 7.5 7.5 0 01-12.548 3.364l-1.902-1.903h3.183a.75.75 0 000-1.5H2.984a.75.75 0 00-.75.75v4.992a.75.75 0 001.5 0v-3.18l1.9 1.9a9 9 0 0015.059-4.035.75.75 0 00-.53-.918z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
          <table className="mt-2 w-full table-fixed rounded-[30px] shadow-lg">
            <thead>
              <tr>
                {['Position', 'Name', 'Office', 'Points'].map(
                  (header, headerIndex) => (
                    <th
                      key={header}
                      className={`border-b-[.7px] border-light-gray px-4 py-4 text-left text-xs font-normal text-dark-gray sm:px-8 ${
                        headerIndex === 0
                          ? 'w-[90px] border-r-[0.7px] sm:w-[117px]'
                          : ''
                      } ${header === 'Points' ? 'w-[117px]' : ''}`}
                    >
                      {header}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {data?.leaderboardUsers.length > 0 ? (
                data?.leaderboardUsers.map((user, index) => (
                  <tr key={`${index}${user.firstName}${user.location}`}>
                    <td className="border-r-[0.7px] border-light-gray px-2 py-3 text-center sm:px-8">
                      {user.firstName !== '' ? index + 1 : ''}
                    </td>
                    <td className="px-4 py-3 font-bold sm:px-8">
                      {`${user.firstName} ${user.lastName}`}
                    </td>
                    <td className="px-4 py-3 sm:px-8">{user.location}</td>
                    <td className="px-4 py-3 sm:px-8">{user.points}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="border-r-[0.7px] border-light-gray px-2 py-3 text-center sm:px-8">
                    -
                  </td>
                  <td className="px-4 py-3 font-bold sm:px-8">-</td>
                  <td className="px-4 py-3 sm:px-8">-</td>
                  <td className="px-4 py-3 sm:px-8">-</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </main>
  )
}

const LoadingSkeleton = () => {
  return (
    <div
      role="status"
      className="animate-pulse mb-[30px] flex flex-row justify-between pt-[24px]"
    >
      <div className="flex flex-col items-center justify-center">
        <div className="mb-[40px] h-2.5 w-[40px] rounded-full bg-gray-300 dark:bg-gray-700"></div>
        <div className="mb-[28px] h-[130px] w-[130px] rounded-full bg-gray-300 dark:bg-gray-700"></div>
        <div className="mb-[16px] h-2.5 w-[60px] rounded-full bg-gray-300 dark:bg-gray-700"></div>
        <div className="h-2.5 w-[100px] rounded-full bg-gray-300 dark:bg-gray-700"></div>
      </div>

      <div className="-mt-[100px] flex flex-col items-center justify-center">
        <div className="mb-[40px] h-2.5 w-[40px] rounded-full bg-gray-300 dark:bg-gray-700"></div>
        <div className="mb-[28px] h-[167px] w-[167px] rounded-full bg-gray-300 dark:bg-gray-700"></div>
        <div className="mb-[16px] h-2.5 w-[60px] rounded-full bg-gray-300 dark:bg-gray-700"></div>
        <div className="h-2.5 w-[100px] rounded-full bg-gray-300 dark:bg-gray-700"></div>
      </div>

      <div className="flex flex-col items-center justify-center">
        <div className="mb-[40px] h-2.5 w-[40px] rounded-full bg-gray-300 dark:bg-gray-700"></div>
        <div className="mb-[28px] h-[130px] w-[130px] rounded-full bg-gray-300 dark:bg-gray-700"></div>
        <div className="mb-[16px] h-2.5 w-[60px] rounded-full bg-gray-300 dark:bg-gray-700"></div>
        <div className="h-2.5 w-[100px] rounded-full bg-gray-300 dark:bg-gray-700"></div>
      </div>
      <span className="sr-only">Loading...</span>
    </div>
  )
}

export default Leaderboard
